import { adoptUserLanguage } from "../i18n";
import api, { storeTokens } from "./api";
import { fetchMe, LOGIN_PLATFORM } from "./authService";

/** Set after a self sign-up, read once by the dashboard's welcome card. */
export const WELCOME_KEY = "businessWelcome";

/**
 * GET /public/plans — the plans the pricing page offers (no auth), the public
 * billing cycles, and the cycle to pre-select. Every price comes computed in
 * `plan.prices[cycleId]`; the portal never works one out itself.
 */
export const fetchPublicPlans = async () => {
  const { data } = await api.get("/public/plans");
  return {
    plans: data.data.plans,
    billingCycles: data.data.billing_cycles ?? [],
    defaultCycleId: data.data.default_billing_cycle_id ?? null,
  };
};

/**
 * POST /public/promo-codes/validate — the plan's quote with the code applied,
 * or a 422 on `promo_code` (or `billing_cycle_id` / `plan_id`). Nothing is
 * redeemed until the sign-up itself.
 */
export const validatePromoCode = async ({ code, planId, cycleId }) => {
  const { data } = await api.post("/public/promo-codes/validate", {
    code,
    plan_id: planId,
    billing_cycle_id: cycleId,
  });
  return data.data.quote;
};

/** The cycle to show for a plan: the wanted one when the plan offers it, else the first it does. */
export const pickCycle = (plan, wanted, fallback) => {
  const offered = Object.keys(plan?.prices ?? {});
  if (wanted && offered.includes(wanted)) return wanted;
  if (fallback && offered.includes(fallback)) return fallback;
  return offered[0] ?? null;
};

/** GET /public/business-types — the types a new business can pick (no auth). */
export const fetchPublicBusinessTypes = async () => {
  const { data } = await api.get("/public/business-types");
  return data.data.business_types;
};

/**
 * POST /public/business-signup. The answer is the same token pair as a login,
 * so the new owner is signed in straight away.
 */
export const signupBusiness = async (payload) => {
  const { data } = await api.post("/public/business-signup", { ...payload, platform: LOGIN_PLATFORM });
  storeTokens(data);

  const user = await fetchMe();
  adoptUserLanguage(user);
  try {
    sessionStorage.setItem(WELCOME_KEY, "1");
  } catch {
    // The hint is a nicety; a blocked storage just skips it.
  }
  return { user };
};

/** A plan's text in the current language, falling back to English. */
export const planText = (value, language) => (value ? value[language] || value.en || "" : "");
